import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/shared/services/account/account.services';
import { TranslationConstants } from 'src/app/shared/services/util/translation.constants';
import { SnackBarTheme } from '../../shared/models/snackbar-theme.enum';
import { SnackbarComponent } from '../../shared/snackbar/snackbar.component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { matchingPasswords, passwordPrerequisites } from '../../shared/validators/password.validator';
import * as Sentry from "@sentry/angular";
@Component({
    selector: 'app-reset',
    templateUrl: './reset.component.html',
    styleUrls: ['../styles/card.style.component.scss']
})
export class ResetComponent implements OnInit {

    public loading: boolean;
    public hasMinChars: boolean;
    public hasLowerCase: boolean;
    public hasUpperCase: boolean;
    public hasNumber: boolean;
    public hasSpecialChar: boolean;
    public hideVisiblePwd: boolean;
    public hideVisibleConfirmPwd: boolean;
    public showPrerequisites: boolean = true;
    public match: boolean = true;
    public showNewPassword: boolean;
    public showRepeatedPassword: boolean;
    public email: string;
    public resetForm: FormGroup = new FormGroup({
        password: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(20), passwordPrerequisites]),
        confirmPassword: new FormControl('', [Validators.required, Validators.minLength(8), Validators.maxLength(20)]),
        code: new FormControl('', [Validators.required])
    }, [matchingPasswords('password', 'confirmPassword')]);

    constructor(
        private readonly location: Location,
        private readonly snackbarComponent: SnackbarComponent,
        private readonly translationConstants: TranslationConstants,
        private readonly accountService: AccountService,
        private readonly router: Router
    ) { }

    public ngOnInit(): void {
        this.accountService.getRsaPublicKey().subscribe();
        this.email = this.accountService.userEmail;
        if (!this.email) {
            this.router.navigate(['/account/forgot']);
        }
    }

    /**
     * To validate confirm password input
     */
    public matchPass(): void {
        this.match = this.resetForm.value.password === this.resetForm.value.confirmPassword;
    }

    /**
     * Validate password prerequisites to show on screen
     */
    public validatePassword(): void {

        if (this.showPrerequisites) {
            const password = this.resetForm.value.password;
            this.hasMinChars = false;
            this.hasLowerCase = false;
            this.hasUpperCase = false;
            this.hasNumber = false;
            this.hasSpecialChar = false;

            if (password.length > 7) {
                this.hasMinChars = true;
            }

            const lowerCaseLetters = /[a-z]/g;
            if (password.match(lowerCaseLetters)) {
                this.hasLowerCase = true;
            }

            const upperCaseLetters = /[A-Z]/g;
            if (password.match(upperCaseLetters)) {
                this.hasUpperCase = true;
            }

            const numbers = /[0-9]/g;
            if (password.match(numbers)) {
                this.hasNumber = true;
            }

            const specialCharacter = /[-!@#$%^*()_+.]/g;
            if (password.match(specialCharacter) && password.length !== 0) {
                this.hasSpecialChar = true;
            }
        }

        if (this.resetForm.value.confirmPassword) {
            this.matchPass();
        }
    }

    public cancel(): void {
        this.location.back();
    }

    public reset(): void {
        this.loading = true;
        Sentry.setUser({ email: this.email });
        this.accountService.resetPassword({secretCode: this.resetForm.value.code,
            newPassword: this.resetForm.value.password,
            email: this.email}).subscribe(success => {
                this.snackbarComponent.openSnackBar(this.translationConstants.translate('account.reset.success'), SnackBarTheme.success);
                this.goToLogin();
            }, err => {
                let errorMessage: string = 'generic';
                if (!err?.error || !err?.error.message) {
                    errorMessage = 'generic';
                } else if (err.error.message.includes('There is no code for the given user')) {
                    errorMessage = 'noCodeForUser'
                } else if (err.error.message.includes('The code is no longer valid')) {
                    errorMessage = 'noLogerValid'
                } else if (err.error.message.includes('The code is not matching')) {
                    errorMessage = 'mismatching'
                }
                this.snackbarComponent.openSnackBar(this.translationConstants.translate(`account.reset.error.${errorMessage}`), SnackBarTheme.error, 4000);
            }).add(() => {
                this.loading = false;
            }
        );
    }

    public goToLogin(): void {
        this.router.navigate(['/account/login']);
    }
}
